import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen, FileText, Download, Play, ArrowRight, Clock,
  Sparkles, Library, Film, ExternalLink, Tag, X, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

// ---- Sample Resources Data ----
const WHITEPAPERS = [
  {
    key: "playbook",
    title: "The Conscious Enterprise Playbook",
    desc: "A practical 28-page guide to building responsible practices across the six pillars.",
    pages: 28,
    type: "PDF",
    accent: "#FDB813",
  },
  {
    key: "framework",
    title: "Six-Pillar Framework Whitepaper",
    desc: "Deep-dive into the methodology, scoring, and benchmarks behind CNESS certification.",
    pages: 42,
    type: "PDF",
    accent: "#3B82F6",
  },
  {
    key: "partner",
    title: "Partner Program Brochure",
    desc: "Everything you need to know about the five CNESS partnership pathways.",
    pages: 16,
    type: "PDF",
    accent: "#10B981",
  },
];

const ARTICLES = [
  {
    title: "Why Trust is the New Currency in Business",
    excerpt: "81% of consumers must trust a brand before purchasing. Here's how CNESS turns trust into a measurable, certifiable asset.",
    readTime: "6 min read",
    category: "Insight",
    date: "Jan 2026",
    body: `Trust used to be a soft signal. Today it is a hard economic input. Edelman's latest data shows 81% of consumers must trust a brand before they will purchase from it, and 87% are willing to pay more for brands they trust.

For business leaders, this changes the operating question. The conversation is no longer "Are we doing good?" but "Can we prove we are doing good — repeatedly, transparently and at scale?"

The CNESS framework was built precisely for that question. By measuring six interconnected pillars — Conscious Leadership, Human-Centric Culture, Products & Services, Customers & Clients, Brand Vision, and Community Impact — we translate values into observable behaviours, and behaviours into a publicly verifiable signal of trust.

Most certifications today reward inputs (policies written, training completed). CNESS rewards outcomes (decisions made under pressure, culture observed by employees, impact felt by community). That distinction is what makes the badge a leading indicator of stakeholder loyalty rather than a lagging indicator of compliance.

The leaders who will compound trust over the next decade will be the ones who invest in the operating system that produces it. CNESS is that operating system.`,
  },
  {
    title: "From Purpose Statements to Verified Practice",
    excerpt: "Stakeholders are tired of vague mission statements. Learn how the Six-Pillar Framework translates intention into evidence.",
    readTime: "8 min read",
    category: "Framework",
    date: "Dec 2025",
    body: `Every company has a purpose statement. Almost none have a purpose system. That is the gap CNESS fills.

A purpose statement says what you believe. A purpose system shows how that belief shapes the decisions you make on a Tuesday afternoon — when a customer wants a discount you cannot afford, when a vendor cuts corners, when a high-performer crosses a culture line.

The CNESS Six-Pillar Framework converts intention into a measurable practice graph. Each pillar carries weighted sub-criteria, each sub-criterion has rubric-based evidence requirements, and each evidence point is independently scored by trained evaluators.

The result: a Conscious Impact Score (CIS) that maps to behaviours, not aspirations. Boards understand it. Investors trust it. Talent rallies around it. And — crucially — it improves year on year, because the certification renewal pressure-tests every claim.

If your purpose has not been verified, it has not been embedded.`,
  },
  {
    title: "The Conscious Leader's Operating System",
    excerpt: "Five practical patterns we see in every Luminary-tier organisation — and how to install them in yours.",
    readTime: "10 min read",
    category: "Leadership",
    date: "Nov 2025",
    body: `After working with hundreds of certified organisations, we have noticed five patterns that consistently separate Luminary-tier leaders from the rest:

1. They publish trade-offs, not just wins. Conscious leaders make their hard choices visible — including the ones that cost them margin in the short term.

2. They run "values reviews" on the same cadence as financial reviews. Every quarter, they pressure-test how decisions stack up against stated values.

3. They externalise feedback loops. Customer panels, employee NPS, supplier audits — all surfaced to leadership unfiltered.

4. They invest in story infrastructure. Stories of values lived (or violated) are documented and re-told, becoming organisational memory.

5. They measure long. Their KPIs include 5-year stakeholder loyalty curves, not just quarterly EPS.

Install these five patterns and the certification largely follows. Skip them and even the best policy library cannot compensate.`,
  },
  {
    title: "Partner Spotlight: Building a Global Conscious Network",
    excerpt: "How CNESS partners are shaping local ecosystems and accelerating responsible business across continents.",
    readTime: "5 min read",
    category: "Partners",
    date: "Oct 2025",
    body: `Our partner network is the connective tissue of the CNESS ecosystem. Today, more than 200 partners across 30 countries help organisations adopt and implement the framework — translating it into local context, language and industry nuance.

This month we're spotlighting three partners: a boutique advisory in Singapore that has guided 40 SMEs through certification, a B-school faculty member in Brazil who runs Inspired-tier prep cohorts, and a media platform in the UAE that broadcasts conscious enterprise stories to 2M monthly readers.

The shared thread is clear: when local leaders carry the framework, conscious enterprise stops being a Western export and starts being a global movement. If you are an advisor, educator, or platform working in this space, the partner program is open and we'd love to hear from you.`,
  },
];

const VIDEOS = [
  {
    id: "VLjNg-kA2N0",
    title: "The Value of CNESS Certification",
    duration: "4:21",
  },
  {
    id: "kkQ9SQ2taps",
    title: "Lead with Purpose, Verified by CNESS",
    duration: "3:47",
  },
  {
    id: "2B7aMYdCo1I",
    title: "The CNESS Ecosystem",
    duration: "5:18",
  },
];

const TABS = [
  { key: "downloads", label: "Downloads", icon: Library },
  { key: "articles", label: "Articles & Insights", icon: BookOpen },
  { key: "videos", label: "Video Library", icon: Film },
];

const ResourcesPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("downloads");
  const [openArticle, setOpenArticle] = useState(null);
  const [downloadResource, setDownloadResource] = useState(null); // { key, title }
  const [subscribeOpen, setSubscribeOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = (openArticle || downloadResource || subscribeOpen) ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [openArticle, downloadResource, subscribeOpen]);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#F8FAFC] to-white" data-testid="resources-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
              <Library className="w-4 h-4 text-[#FDB813]" />
              <span className="text-sm font-medium text-[#1E3A5F]">CNESS Resources</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1E3A5F] mb-6">
              Insights for the <span className="text-[#FDB813]">Conscious Leader</span>
            </h1>
            <p className="text-lg text-gray-600">
              Whitepapers, articles, and videos to help your organisation move from "we care" to "we can show how".
            </p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b bg-white sticky top-20 lg:top-24 z-30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 sm:gap-8 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`resources-tab-${t.key}`}
                className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center ${
                  tab === t.key
                    ? "border-[#1E3A5F] text-[#1E3A5F]"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <t.icon className="w-4 h-4 mr-2" />
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {tab === "downloads" && (
            <div data-testid="resources-downloads">
              <div className="grid md:grid-cols-3 gap-6">
                {WHITEPAPERS.map((wp, i) => (
                  <div
                    key={i}
                    className="bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-lg transition-all flex flex-col"
                  >
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                      style={{ backgroundColor: `${wp.accent}15` }}
                    >
                      <FileText className="w-7 h-7" style={{ color: wp.accent }} />
                    </div>
                    <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                      <span className="px-2 py-1 bg-gray-100 rounded font-medium">{wp.type}</span>
                      <span>{wp.pages} pages</span>
                    </div>
                    <h3 className="text-lg font-bold text-[#1E3A5F] mb-2">{wp.title}</h3>
                    <p className="text-sm text-gray-600 mb-6 flex-1">{wp.desc}</p>
                    <Button
                      data-testid={`resources-download-btn-${wp.key}`}
                      onClick={() => setDownloadResource({ key: wp.key, title: wp.title })}
                      variant="outline"
                      className="border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white w-full"
                    >
                      <Download className="w-4 h-4 mr-2" /> Request Download
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-center text-sm text-gray-500 mt-8">
                Downloads are curated for serious enquirers. Submit a request and we'll email the link.
              </p>
            </div>
          )}

          {tab === "articles" && (
            <div className="grid md:grid-cols-2 gap-6" data-testid="resources-articles">
              {ARTICLES.map((a, i) => (
                <div
                  key={i}
                  onClick={() => setOpenArticle(a)}
                  data-testid={`article-card-${i}`}
                  className="group bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-lg hover:border-[#FDB813]/30 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3 mb-4 text-xs">
                    <span className="px-3 py-1 bg-[#FEF3C7] text-[#1E3A5F] rounded-full font-medium flex items-center">
                      <Tag className="w-3 h-3 mr-1" /> {a.category}
                    </span>
                    <span className="text-gray-400 flex items-center">
                      <Clock className="w-3 h-3 mr-1" /> {a.readTime}
                    </span>
                    <span className="text-gray-400">· {a.date}</span>
                  </div>
                  <h3 className="text-xl font-bold text-[#1E3A5F] mb-3 group-hover:text-[#FDB813] transition-colors">
                    {a.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">{a.excerpt}</p>
                  <div className="flex items-center text-sm font-semibold text-[#1E3A5F] group-hover:text-[#FDB813]">
                    Read article <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "videos" && (
            <div data-testid="resources-videos">
              <div className="grid md:grid-cols-3 gap-6 mb-10">
                {VIDEOS.map((v) => (
                  <div
                    key={v.id}
                    className="group cursor-pointer rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all bg-white border border-gray-100"
                    onClick={() => navigate("/gallery")}
                  >
                    <div className="relative aspect-video bg-black">
                      <img
                        src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`}
                        alt={v.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                        <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
                          <Play className="w-6 h-6 text-[#1E3A5F] ml-1" />
                        </div>
                      </div>
                      <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {v.duration}
                      </span>
                    </div>
                    <div className="p-5">
                      <h4 className="font-semibold text-[#1E3A5F]">{v.title}</h4>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <Button
                  onClick={() => navigate("/gallery")}
                  data-testid="resources-view-gallery-btn"
                  className="bg-[#1E3A5F] hover:bg-[#152a45] text-white px-8 py-3 rounded-xl"
                >
                  View Full Gallery <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1E3A5F] to-[#0F172A] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Sparkles className="w-12 h-12 text-[#FDB813] mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Stay informed</h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter for monthly insights on conscious enterprise practice.
          </p>
          <Button
            onClick={() => setSubscribeOpen(true)}
            data-testid="resources-newsletter-btn"
            className="bg-[#FDB813] hover:bg-[#e5a611] text-[#1E3A5F] font-semibold px-8 py-3 rounded-xl"
          >
            Subscribe to Newsletter <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>
      {/* Article Reader Modal */}
      {openArticle && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setOpenArticle(null)}
          data-testid="article-modal-overlay"
        >
          <div
            className="bg-white rounded-3xl max-w-3xl w-full mt-4 sm:mt-8 mb-8 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="article-modal"
          >
            <div className="relative bg-gradient-to-br from-[#1E3A5F] to-[#152a45] text-white p-8">
              <button
                onClick={() => setOpenArticle(null)}
                data-testid="article-close-btn"
                className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center"
                aria-label="Close article"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3 mb-3 text-xs">
                <span className="px-3 py-1 bg-[#FDB813] text-[#1E3A5F] rounded-full font-semibold">
                  {openArticle.category}
                </span>
                <span className="text-white/70 flex items-center">
                  <Clock className="w-3 h-3 mr-1" /> {openArticle.readTime}
                </span>
                <span className="text-white/70">· {openArticle.date}</span>
              </div>
              <h2 className="text-3xl font-bold leading-tight">{openArticle.title}</h2>
            </div>
            <div className="p-8 space-y-4">
              {openArticle.body.split("\n\n").map((para, i) => (
                <p key={i} className="text-gray-700 leading-relaxed text-base">{para}</p>
              ))}
            </div>
            <div className="p-6 bg-[#F8FAFC] border-t border-gray-100 flex flex-col sm:flex-row gap-3 justify-between items-center">
              <p className="text-sm text-gray-500">Liked this? Subscribe to get the next one in your inbox.</p>
              <div className="flex gap-3">
                <Button
                  onClick={() => { setOpenArticle(null); setSubscribeOpen(true); }}
                  data-testid="article-reader-subscribe-btn"
                  className="bg-[#1E3A5F] hover:bg-[#152a45] text-white rounded-xl"
                >
                  Subscribe <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setOpenArticle(null)}
                  className="border-2 border-[#1E3A5F] text-[#1E3A5F] rounded-xl"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resource Download Modal */}
      {downloadResource && (
        <ResourceDownloadModal
          resource={downloadResource}
          onClose={() => setDownloadResource(null)}
        />
      )}

      {/* Newsletter Subscribe Modal */}
      {subscribeOpen && (
        <NewsletterSubscribeModal onClose={() => setSubscribeOpen(false)} />
      )}
    </PublicLayout>
  );
};

/* ---------- Resource Download Modal (double opt-in for any resource) ---------- */
const ResourceDownloadModal = ({ resource, onClose }) => {
  const [form, setForm] = useState({ full_name: "", email: "", organization: "", remarks: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/resources/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, resource: resource.key }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === "string" ? data.detail : "Submission failed.");
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="resource-modal-overlay"
    >
      <div
        className="bg-white rounded-3xl max-w-xl w-full mt-4 sm:mt-8 mb-8 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid={`resource-modal-${resource.key}`}
      >
        <div className="relative bg-gradient-to-br from-[#1E3A5F] to-[#152a45] text-white p-7">
          <button
            onClick={onClose}
            data-testid="resource-modal-close"
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FDB813]/20 rounded-full mb-3">
            <FileText className="w-4 h-4 text-[#FDB813]" />
            <span className="text-xs font-semibold text-[#FDB813] uppercase tracking-widest">CNESS Resource</span>
          </div>
          <h2 className="text-2xl font-bold leading-tight">{resource.title}</h2>
          <p className="text-white/70 text-sm mt-2">Tell us a little about you — we'll send a confirmation link to your email so the download stays secure.</p>
        </div>

        <div className="p-7">
          {submitted ? (
            <div className="text-center py-6" data-testid="resource-modal-success">
              <div className="w-14 h-14 bg-[#FDB813] rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1E3A5F] mb-2">Check your inbox</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                We've sent a confirmation link to <strong className="text-[#1E3A5F]">{form.email}</strong>.
                Click it within 24 hours and your <strong>{resource.title}</strong> PDF will be on its way.
              </p>
              <Button
                onClick={onClose}
                className="bg-[#1E3A5F] hover:bg-[#152a45] text-white rounded-xl px-6 py-3"
                data-testid="resource-modal-done"
              >
                Got it
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="resource-form">
              <Field label="Full name" required>
                <input data-testid="resource-name" required value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)} className={modalInputCls} placeholder="Jane Doe" />
              </Field>
              <Field label="Organisation" required>
                <input data-testid="resource-org" required value={form.organization}
                  onChange={(e) => update("organization", e.target.value)} className={modalInputCls} placeholder="Acme Inc." />
              </Field>
              <Field label="Work email" required>
                <input data-testid="resource-email" required type="email" value={form.email}
                  onChange={(e) => update("email", e.target.value)} className={modalInputCls} placeholder="you@company.com" />
              </Field>
              <Field label="Remarks (optional)">
                <textarea data-testid="resource-remarks" value={form.remarks}
                  onChange={(e) => update("remarks", e.target.value)}
                  className={`${modalInputCls} min-h-[90px] resize-y`}
                  placeholder="Anything you'd like our team to know" />
              </Field>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm" data-testid="resource-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                data-testid="resource-submit"
                className="w-full bg-[#1E3A5F] hover:bg-[#152a45] text-white py-5 rounded-xl"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending verification…</>
                ) : (
                  <>Send Me the Verification Link <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                We'll only use your email to send the resource and the occasional CNESS update. Unsubscribe anytime.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- Newsletter Subscribe Modal ---------- */
const NewsletterSubscribeModal = ({ onClose }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === "string" ? data.detail : "Subscription failed.");
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="newsletter-modal-overlay"
    >
      <div
        className="bg-white rounded-3xl max-w-md w-full mt-8 sm:mt-20 mb-8 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="newsletter-modal"
      >
        <div className="relative bg-gradient-to-br from-[#1E3A5F] to-[#152a45] text-white p-7">
          <button
            onClick={onClose}
            data-testid="newsletter-modal-close"
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FDB813]/20 rounded-full mb-3">
            <Sparkles className="w-4 h-4 text-[#FDB813]" />
            <span className="text-xs font-semibold text-[#FDB813] uppercase tracking-widest">CNESS Newsletter</span>
          </div>
          <h2 className="text-2xl font-bold leading-tight">Conscious Leader · Monthly</h2>
          <p className="text-white/70 text-sm mt-2">One practical insight, one tool, one Luminary story. No fluff.</p>
        </div>

        <div className="p-7">
          {submitted ? (
            <div className="text-center py-4" data-testid="newsletter-modal-success">
              <div className="w-14 h-14 bg-[#FDB813] rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1E3A5F] mb-2">You're in!</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                We've sent a welcome note to <strong className="text-[#1E3A5F]">{email}</strong>.
                See you next month.
              </p>
              <Button
                onClick={onClose}
                className="bg-[#1E3A5F] hover:bg-[#152a45] text-white rounded-xl px-6 py-3"
                data-testid="newsletter-modal-done"
              >
                Got it
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="newsletter-form">
              <Field label="Email address" required>
                <input
                  data-testid="newsletter-email"
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={modalInputCls}
                  placeholder="you@company.com"
                />
              </Field>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm" data-testid="newsletter-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                data-testid="newsletter-submit"
                className="w-full bg-[#FDB813] hover:bg-[#e5a611] text-[#1E3A5F] font-semibold py-5 rounded-xl"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Subscribing…</>
                ) : (
                  <>Subscribe <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                We send one email a month. Unsubscribe anytime.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-[#1E3A5F] mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const modalInputCls =
  "w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#FDB813] focus:ring-2 focus:ring-[#FDB813]/20 text-[#1E3A5F] placeholder:text-gray-400 bg-white";

export default ResourcesPage;
